import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { runInsight, downloadInsightCsv } from '../api/modulesApi';
import { errorMessage } from '../api/securityApi';
import ModuleNav from '../components/Modules/ModuleNav';
import SiteSelector, { useActiveSite } from '../components/Security/SiteSelector';
import InsightView from '../components/Modules/InsightView';

// Extra query parameters that specific insights understand.
const EXTRA = {
  'click.timeline': [['sessionId', 'Session ID']],
  'ml.explain': [['threatId', 'Threat ID']],
  'audience.dimensions': [['field', 'Event field (e.g. cls)'], ['type', 'Event type (e.g. click)']],
  'audience.search': [['ownHost', 'Your domain (to exclude)']],
  'adsense.earnings': [['domain', 'Site']], 'adsense.breakdown': [['domain', 'Site']], 'adsense.rpm': [['domain', 'Site']],
  'adsense.forecast': [['domain', 'Site']], 'adsense.placement': [['domain', 'Site']],
};
const LIVE = ['dash.live', 'dash.realtime', 'dash.map'];

export default function InsightPage() {
  const { key } = useParams();
  const [sp, setSp] = useSearchParams();
  const { sites, siteId, setSiteId } = useActiveSite();
  const [days, setDays] = useState(Number(sp.get('days')) || 7);
  const [extra, setExtra] = useState(() => Object.fromEntries(sp.entries()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [auto, setAuto] = useState(LIVE.includes(key));
  const timer = useRef(null);

  const params = useCallback(() => {
    const p = { siteId, days };
    (EXTRA[key] || []).forEach(([k]) => { if (extra[k]) p[k] = extra[k]; });
    return p;
  }, [siteId, days, extra, key]);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { setData(await runInsight(key, params())); }
    catch (e) { setError(errorMessage(e)); setData(null); }
    finally { setLoading(false); }
  }, [key, params]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    clearInterval(timer.current);
    if (auto) timer.current = setInterval(load, 15000);
    return () => clearInterval(timer.current);
  }, [auto, load]);

  const setExtraVal = (k, v) => {
    setExtra((x) => ({ ...x, [k]: v }));
    const next = new URLSearchParams(sp); if (v) next.set(k, v); else next.delete(k);
    setSp(next, { replace: true });
  };

  return (
    <div className="shell">
      <header className="page-header">
        <div>
          <h1>{data?.title || key}</h1>
          <p className="muted">{data?.description}{data ? <> · <Link to="/modules">All modules</Link></> : null}</p>
        </div>
      </header>
      <ModuleNav />
      <div className="sec-toolbar">
        <SiteSelector sites={sites} value={siteId} onChange={setSiteId} />
        <select className="sec-select" value={days} onChange={(e) => setDays(Number(e.target.value))}>
          {[1, 3, 7, 14, 30, 60, 90].map((d) => <option key={d} value={d}>Last {d} day{d > 1 ? 's' : ''}</option>)}
        </select>
        {(EXTRA[key] || []).map(([k, label]) => (
          <input key={k} className="sec-input" placeholder={label} value={extra[k] || ''} onChange={(e) => setExtraVal(k, e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} />
        ))}
        <button className="ghost" onClick={load} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</button>
        <label className="muted"><input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} /> Auto-refresh (15 s)</label>
        <button className="ghost" onClick={() => downloadInsightCsv(key, params()).catch((e) => setError(errorMessage(e)))}>Export CSV</button>
        <button className="ghost" onClick={() => window.print()}>Print / PDF</button>
      </div>
      {error && <div className="toast error">{error}</div>}
      {loading && !data && <p className="muted">Loading…</p>}
      {data && <InsightView result={data.result} />}
    </div>
  );
}
